import React, { useState, useRef } from "react";
import { Form, InputGroup, Button, Spinner } from "react-bootstrap";
import { updateDocumentAltText } from "../../utils/DocumentHelper";

/**
 * Reusable small UI block for managing and optionally updating
 * the alt text of an image / document.
 *
 * - If `documentId` is provided AND `showUpdateButton` is true,
 *   it can show an "Update" button that calls the API.
 * - If `documentId` is missing OR `showUpdateButton` is false,
 *   it only behaves like a normal controlled text input.
 *
 * It can work in two modes:
 * - Controlled: pass `value` and `onChange` (recommended).
 * - Uncontrolled: rely on `initialAltText` (internal state).
 */
const AltTextUpdater = ({
  documentId,
  value,
  onChange,
  initialAltText = "",
  label = "Image Alt Text",
  showUpdateButton = true,
  // When true, hide the entire input until there is a valid documentId.
  hideInputWithoutId = false,
  onUpdated,
}) => {
  const isControlled = typeof value !== "undefined" && typeof onChange === "function";
  const [internalAltText, setInternalAltText] = useState(initialAltText || "");
  const [isSaving, setIsSaving] = useState(false);
  const [showUpdateAction, setShowUpdateAction] = useState(false);
  const typingTimeoutRef = useRef(null);

  const currentAltText = isControlled ? value : internalAltText;
  const canUpdate = !!documentId && showUpdateButton;
  const shouldRenderInput = !hideInputWithoutId || !!documentId;

  // Optionally hide the whole block when we must have an ID but do not yet.
  if (!shouldRenderInput) {
    return null;
  }

  const handleChange = (event) => {
    const nextValue = event.target.value;
    if (isControlled) {
      onChange(nextValue);
    } else {
      setInternalAltText(nextValue);
    }

    // If updating is not enabled for this instance, no timer / button logic.
    if (!canUpdate) {
      return;
    }

    // While the user is actively typing, keep the button hidden.
    setShowUpdateAction(false);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // After 2 seconds of no typing, show the update button.
    if (nextValue && nextValue.trim() !== "") {
      typingTimeoutRef.current = setTimeout(() => {
        setShowUpdateAction(true);
      }, 2000);
    }
  };

  const handleUpdate = async () => {
    if (!canUpdate) {
      return;
    }

    setIsSaving(true);
    try {
      const result = await updateDocumentAltText(documentId, currentAltText);

      if (result && typeof onUpdated === "function") {
        onUpdated(currentAltText, result);
      }

      // On successful update, immediately hide the button again.
      // It will re-appear only after the user edits the text and pauses.
      setShowUpdateAction(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="alt-text-updater mt-2">
      {/* {label && <Form.Label>{label}</Form.Label>} */}
      <InputGroup className="mb-2">
        <Form.Control
          type="text"
          placeholder="Describe the image for SEO and accessibility"
          value={currentAltText}
          onChange={handleChange}
          disabled={isSaving && canUpdate}
          style={{ backgroundColor: "#FAFAFA", height: "42px" }}
        />
        {canUpdate && showUpdateAction && (
          <Button
            variant="primary"
            onClick={handleUpdate}
            disabled={isSaving}
            style={{ whiteSpace: "nowrap" }}
          >
            {isSaving ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-1"
                />
                Saving...
              </>
            ) : (
              "Update"
            )}
          </Button>
        )}
      </InputGroup>
    </div>
  );
};

export default AltTextUpdater;


